import { z } from 'zod'

import { ensureRouteData, prefetchRouteData } from '@/app/query-client'
import {
  certificatesQueryOptions,
  coursePaymentsQueryOptions,
  courseListQueryOptions,
  myLearningQueryOptions,
  purchasedCoursesQueryOptions,
} from '@/features/courses/api/queries'
import type { RouterContext } from '@/routes/layouts/root'

/** The three tabs under the stat strip, in the order they are drawn. */
export const MY_LEARNING_TABS = ['in-progress', 'finished', 'purchases'] as const
export const myLearningTabSchema = z.enum(MY_LEARNING_TABS)
export type MyLearningTab = z.infer<typeof myLearningTabSchema>

/** `tab` — In progress (default), Finished or Purchases. */
export const myLearningSearchSchema = z.object({
  tab: myLearningTabSchema.default('in-progress').catch('in-progress'),
})
export type MyLearningSearch = z.infer<typeof myLearningSearchSchema>

export const MY_LEARNING_SEARCH_DEFAULTS = { tab: 'in-progress' } as const

/**
 * The `/courses/my-learning` loader.
 *
 * MAIN: the progress rollup — the stat strip's first two figures and both
 * progress tabs read it.
 *
 * SECONDARY, only for a real navigation: the certificates (the strip's third
 * figure) and the catalog (the Pick up next shelf), each drawing its own
 * placeholder or nothing; and the purchases list and the course payments
 * list, which only the Purchases tab draws (each with its own skeleton).
 * Started whatever the tab, so a click on Purchases usually finds both
 * cached, and the payment-return page's "See course payments" link, which
 * opens that tab, has them in flight beside the rollup.
 */
export async function loadMyLearning({
  context: { queryClient },
  preload,
}: {
  context: RouterContext
  preload: boolean
}): Promise<void> {
  if (!preload) {
    prefetchRouteData(queryClient, certificatesQueryOptions())
    prefetchRouteData(queryClient, courseListQueryOptions())
    prefetchRouteData(queryClient, purchasedCoursesQueryOptions())
    prefetchRouteData(queryClient, coursePaymentsQueryOptions())
  }
  await ensureRouteData(queryClient, myLearningQueryOptions())
}
